import { RoutesConstants } from "@/main/routes/routesConstants";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { bodyshopApi, quoteApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { InsurerFilterRequest } from "@/main/models/request";
import { BodyshopRegistrationDataResponse, PreQuoteResponse } from "@/main/models/response";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { getDefaultCountryOptionDrop, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers } from "@/main/store/insurer";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

interface IPreQuoteProvider {
	children: React.ReactNode;
}

interface IPreQuoteContext {
	openModalCancel: boolean;
	isLoadingInfoOrder: boolean;
	isLoadingBodyshop: boolean;
	setOpenModalCancel: (value: boolean) => void;
	displayModalCancel: () => void;
	guidQuote: string | null;
	preQuoteData: PreQuoteResponse | null;
	bodyshopData: BodyshopRegistrationDataResponse | null;
	isChangedVehicleForm: boolean;
	setIsChangedVehicleForm: (value: boolean) => void;
}

type INTIAL_FORM_STATE_GLASSType = {
	guidQuoteItem: string;
	partnumber: string;
	code: string;
	refPrice: number;
	notes: string;
};
export type IPreQuoteFormGlass = INTIAL_FORM_STATE_GLASSType;
export type IKeysPreQuoteFormGlass = keyof IPreQuoteFormGlass;

type INITIAL_FORM_STATE_TIREType = {
	guidQuoteItem: string;
	partnumber: string;
	brand: KeyValue | null;
	model: string;
	width: string;
	height: string;
	rim: string;
	range: string;
	refPrice: number;
	notes: string;
	isChangedVehicleForm: boolean;
};

export type IPreQuoteFormTire = INITIAL_FORM_STATE_TIREType;
export type IKeysPreQuoteFormTire = keyof IPreQuoteFormTire;

const INITIAL_FORM_STATE = {
	status: null as KeyValue | null,

	// vehicle
	vehicleCountry: null as KeyValue | null,
	manufacturer: null as KeyValue | null,
	model: null as KeyValue | null,
	trim: null as KeyValue | null,
	year: null as KeyValue | null,
	isChangedVehicleForm: false as boolean,

	// bodyshop
	country: null as KeyValue | null | undefined,
	companyName: "",
	rfc: "",
	tradeName: "",

	glasses: [] as IPreQuoteFormGlass[],
	tires: [] as IPreQuoteFormTire[],
};

export type IPreQuoteForm = typeof INITIAL_FORM_STATE;

const PreQuoteContext = createContext<IPreQuoteContext>({
	openModalCancel: false,
	isLoadingInfoOrder: false,
	isLoadingBodyshop: false,
	setOpenModalCancel: () => {},
	displayModalCancel: () => {},
	guidQuote: "",
	preQuoteData: null,
	bodyshopData: null,
	isChangedVehicleForm: false,
	setIsChangedVehicleForm: () => {},
});

function PreQuoteProvider({ children }: IPreQuoteProvider) {
	const methods = useForm<IPreQuoteForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});
	const [searchParams] = useSearchParams();
	const guidQuote = searchParams.get("guidQuote");
	const [openModalCancel, setOpenModalCancel] = useState<boolean>(false);
	const [isLoadingInfoOrder, setIsLoadingInfoOrder] = useState(false);
	const [isLoadingBodyshop, setIsLoadingBodyshop] = useState(false);
	const [preQuoteData, setPreQuoteData] = useState<PreQuoteResponse | null>(null);
	const [bodyshopData, setBodyshopData] = useState<BodyshopRegistrationDataResponse | null>(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { countriesUser: countryReducer } = useCountryReducer();

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);
	const [isChangedVehicleForm, setIsChangedVehicleForm] = useState<boolean>(false);

	function displayModalCancel() {
		setOpenModalCancel(true);
	}

	function loadInfoBodyshop(guidBodyshop: string) {
		setIsLoadingBodyshop(true);
		bodyshopApi
			.getRegistrationDataBodyshop({ guidBodyShop: guidBodyshop })
			.then((response) => {
				const resultData = response.data.result;
				setBodyshopData(resultData);
			})
			.finally(() => {
				setIsLoadingBodyshop(false);
			});
	}

	async function loadInfoOrder() {
		if (guidQuote) {
			setIsLoadingInfoOrder(true);
			try {
				const {
					data: { result },
				} = await quoteApi.getPreQuote(guidQuote);
				setIsLoadingInfoOrder(false);

				if (result) {
					setPreQuoteData(result);
					if (!isNullUndefined(result.guidBodyShop)) loadInfoBodyshop(result.guidBodyShop);
				} else {
					toast.error(getTranslateByKey("information.quoteNotFound", true));
				}
			} finally {
				setIsLoadingInfoOrder(false);
			}
		} else {
			navigate(RoutesConstants.notFound);
		}
	}

	useEffect(() => {
		loadInfoOrder();
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(optionDefaultCountryDrop)) {
			const request: InsurerFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getInsurers(request));
		}
	}, [optionDefaultCountryDrop?.key]);

	return (
		<FormProvider {...methods}>
			<PreQuoteContext.Provider
				value={{
					guidQuote,
					displayModalCancel,
					openModalCancel,
					isLoadingInfoOrder,
					setOpenModalCancel,
					preQuoteData,
					bodyshopData,
					isLoadingBodyshop,
					isChangedVehicleForm,
					setIsChangedVehicleForm,
				}}
			>
				{children}
			</PreQuoteContext.Provider>
		</FormProvider>
	);
}

const usePreQuoteContext = () => useContext(PreQuoteContext);

export { PreQuoteProvider, usePreQuoteContext };
